import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  buildCalendarCells,
  computeDueDatePickerFixedStyle,
  formatIsoDate,
  parseIsoDateParts,
} from "./ActionItemDueDatePicker";

const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const getTodayIso = () => {
  const d = new Date();
  return formatIsoDate(d.getFullYear(), d.getMonth(), d.getDate());
};

const formatShort = (iso) => {
  if (!iso) return "";
  const parts = parseIsoDateParts(iso);
  if (!parts) return "";
  const currentYear = new Date().getFullYear();
  const label = `${MONTH_SHORT[parts.monthIndex]} ${parts.day}`;
  return parts.year === currentYear ? label : `${label}, ${parts.year}`;
};

/**
 * Range date picker — two independent From / To slots.
 * Clicking a slot header activates it; clicking a calendar day sets that slot's date.
 * After setting From, auto-switches to To. Outside-click applies with today as fallback
 * for any unset slot.
 */
const ActionItemsDateRangePicker = ({ from, to, onChange, onClose, anchorRef = null }) => {
  const rootRef = useRef(null);
  const [fixedStyle, setFixedStyle] = useState(null);

  const [localFrom, setLocalFrom] = useState(from || "");
  const [localTo, setLocalTo] = useState(to || "");
  const [activeSlot, setActiveSlot] = useState("from");
  const [hoverDate, setHoverDate] = useState("");

  const initialParts = useMemo(() => {
    return parseIsoDateParts(from) || { year: new Date().getFullYear(), monthIndex: new Date().getMonth() };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [viewYear, setViewYear] = useState(initialParts.year);
  const [viewMonthIndex, setViewMonthIndex] = useState(initialParts.monthIndex);

  // Portal positioning
  const updateFixedPosition = useCallback(() => {
    if (!anchorRef?.current) return;
    const next = computeDueDatePickerFixedStyle(anchorRef.current, rootRef.current, {
      placement: "auto",
      align: "start",
    });
    setFixedStyle(next);
  }, [anchorRef]);

  useLayoutEffect(() => {
    updateFixedPosition();
    const el = rootRef.current;
    const ro = typeof ResizeObserver !== "undefined" && el ? new ResizeObserver(updateFixedPosition) : null;
    ro?.observe(el);
    window.addEventListener("resize", updateFixedPosition);
    window.addEventListener("scroll", updateFixedPosition, true);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", updateFixedPosition);
      window.removeEventListener("scroll", updateFixedPosition, true);
    };
  }, [updateFixedPosition, viewYear, viewMonthIndex]);

  const applyAndClose = useCallback((overrideFrom, overrideTo) => {
    let f = overrideFrom !== undefined ? overrideFrom : localFrom;
    let t = overrideTo !== undefined ? overrideTo : localTo;
    const today = getTodayIso();
    if (f && !t) t = today;
    if (t && !f) f = today;
    if (f && t && f > t) [f, t] = [t, f];
    onChange(f, t);
    onClose();
  }, [localFrom, localTo, onChange, onClose]);

  // Outside click → apply with today fallback
  useEffect(() => {
    const handler = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      applyAndClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [applyAndClose, anchorRef]);

  const handleDayClick = useCallback((iso) => {
    if (activeSlot === "from") {
      setLocalFrom(iso);
      setActiveSlot("to");
    } else {
      setLocalTo(iso);
      applyAndClose(localFrom, iso);
    }
  }, [activeSlot, localFrom, applyAndClose]);

  const handleClear = () => {
    onChange("", "");
    onClose();
  };

  const shiftMonth = (delta) => {
    const anchor = new Date(viewYear, viewMonthIndex + delta, 1);
    setViewYear(anchor.getFullYear());
    setViewMonthIndex(anchor.getMonth());
  };

  const cells = useMemo(
    () => buildCalendarCells(viewYear, viewMonthIndex),
    [viewYear, viewMonthIndex]
  );

  const today = useMemo(() => getTodayIso(), []);

  // Resolve the range to highlight (use hover preview when setting To)
  const previewTo = activeSlot === "to" && hoverDate ? hoverDate : localTo;
  const rangeA = localFrom;
  const rangeB = previewTo;
  const effectiveFrom = rangeA && rangeB ? (rangeA <= rangeB ? rangeA : rangeB) : rangeA || rangeB;
  const effectiveTo = rangeA && rangeB ? (rangeA <= rangeB ? rangeB : rangeA) : "";

  const pickerNode = (
    <div
      ref={rootRef}
      className={[
        "action-item-due-picker",
        anchorRef ? "action-item-due-picker--portaled" : "",
        fixedStyle?.openAbove ? "action-item-due-picker--above" : "",
      ].filter(Boolean).join(" ")}
      role="dialog"
      aria-label="Choose date range"
      style={
        anchorRef
          ? {
              position: "fixed",
              top: fixedStyle?.top ?? -9999,
              left: fixedStyle?.left ?? -9999,
              visibility: fixedStyle ? "visible" : "hidden",
              zIndex: 10000,
            }
          : undefined
      }
      onClick={(e) => e.stopPropagation()}
    >
      {/* Slot headers — clickable to switch which date is being set */}
      <div className="action-item-due-range-picker__slots">
        <button
          type="button"
          className={`action-item-due-range-picker__slot${
            activeSlot === "from" ? " action-item-due-range-picker__slot--active" : ""
          }`}
          onClick={() => setActiveSlot("from")}
          aria-label="Select start date"
        >
          <span className="action-item-due-range-picker__slot-label">From</span>
          <span className="action-item-due-range-picker__slot-value">
            {formatShort(localFrom) || "–"}
          </span>
        </button>
        <span className="action-item-due-range-picker__arrow" aria-hidden="true">→</span>
        <button
          type="button"
          className={`action-item-due-range-picker__slot${
            activeSlot === "to" ? " action-item-due-range-picker__slot--active" : ""
          }`}
          onClick={() => setActiveSlot("to")}
          aria-label="Select end date"
        >
          <span className="action-item-due-range-picker__slot-label">To</span>
          <span className="action-item-due-range-picker__slot-value">
            {formatShort(localTo) || "–"}
          </span>
        </button>
      </div>

      {/* Month navigation */}
      <div className="action-item-due-picker__nav">
        <button
          type="button"
          className="action-item-due-picker__nav-btn"
          onClick={() => shiftMonth(-1)}
          aria-label="Previous month"
        >
          ‹
        </button>
        <span className="action-item-due-picker__month-label">
          {MONTH_NAMES[viewMonthIndex]} {viewYear}
        </span>
        <button
          type="button"
          className="action-item-due-picker__nav-btn"
          onClick={() => shiftMonth(1)}
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      {/* Weekday headers */}
      <div className="action-item-due-picker__weekdays" aria-hidden="true">
        {WEEKDAY_LABELS.map((label) => (
          <span key={label} className="action-item-due-picker__weekday">{label}</span>
        ))}
      </div>

      {/* Day grid */}
      <div className="action-item-due-picker__grid">
        {cells.map((cell, index) => {
          const iso = formatIsoDate(cell.year, cell.monthIndex, cell.day);
          const isStart = effectiveFrom && iso === effectiveFrom;
          const isEnd = effectiveTo && iso === effectiveTo;
          const isInRange = effectiveFrom && effectiveTo && iso > effectiveFrom && iso < effectiveTo;
          const isToday = iso === today;

          const classNames = [
            "action-item-due-picker__day",
            cell.inMonth ? "" : "action-item-due-picker__day--outside",
            isToday ? "action-item-due-picker__day--today" : "",
            isStart ? "action-item-due-picker__day--range-start" : "",
            isEnd && !isStart ? "action-item-due-picker__day--range-end" : "",
            isInRange ? "action-item-due-picker__day--in-range" : "",
          ].filter(Boolean).join(" ");

          return (
            <button
              key={`${iso}-${index}`}
              type="button"
              className={classNames}
              onClick={() => handleDayClick(iso)}
              onMouseEnter={() => setHoverDate(iso)}
              onMouseLeave={() => setHoverDate("")}
              aria-label={iso}
              aria-pressed={isStart || isEnd}
            >
              {cell.day}
            </button>
          );
        })}
      </div>

      {(localFrom || localTo) ? (
        <button
          type="button"
          className="action-item-due-picker__clear"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleClear}
        >
          Clear date filter
        </button>
      ) : null}
    </div>
  );

  return anchorRef ? createPortal(pickerNode, document.body) : pickerNode;
};

export default ActionItemsDateRangePicker;
